import { css } from '@emotion/react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { GetWeavePopNotificationByType, NotificationActions } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Chip, InlineNotification, Text } from '@frontend/design-system';
import { useNotificationContext } from '../notification-provider';
import { BaseNotificationComponentProps, BaseNotificationInner, BaseNotificationOuter } from './base';

type Props = BaseNotificationComponentProps<GetWeavePopNotificationByType<'voicemail-tag'>>;

export const VoicemailTagNotificationComponent = ({ isHistorical, notification, emit, ...rest }: Props) => {
  const { t } = useTranslation();

  const {
    voicemailMessageId,
    mailboxId,
    mailboxName,
    voicemailCreatedAt,
    personContact,
    callerNumber,
    tagsNotificationPayload,
    locationId,
    userHasAccess,
  } = notification.payload;
  const notificationTrayContext = useNotificationContext();

  const markRead = () =>
    notificationTrayContext.markVoicemailNotificationsAsRead(
      voicemailMessageId,
      mailboxId,
      NotificationType.VOICEMAIL_TAG,
      locationId
    );

  const actions: NotificationActions = [
    {
      label: t('View'),
      action: () => {
        emit({ action: 'view', payload: undefined }, notification);
        markRead();
      },
    },
  ];

  return (
    <BaseNotificationOuter
      actions={actions}
      notification={notification}
      emit={emit}
      isHistorical={isHistorical}
      {...rest}
    >
      <BaseNotificationInner
        onClose={() => emit({ action: 'ignore', payload: undefined }, notification)}
        title={t(`Tag Applied to Voicemail`)}
        actions={actions}
        body={
          <section
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: ${theme.spacing(1)};
            `}
          >
            <Chip.Tag color={tagsNotificationPayload[0]?.color}>{tagsNotificationPayload[0]?.name}</Chip.Tag>
            <Text
              weight='bold'
              size='medium'
              css={css`
                margin: 0;
              `}
            >
              {personContact || callerNumber}
            </Text>
            {userHasAccess && (
              <>
                <Text
                  size='medium'
                  css={css`
                    margin: 0;
                  `}
                >
                  {dayjs(voicemailCreatedAt).format('MMM DD YYYY, hh:mm A')}
                </Text>

                <div
                  css={css`
                    display: flex;
                    gap: ${theme.spacing(0.5)};
                    align-items: center;
                  `}
                >
                  <Icon name='voicemail-small' />
                  <Text
                    size='medium'
                    css={css`
                      margin: 0;
                    `}
                  >
                    {mailboxName}
                  </Text>
                </div>
              </>
            )}
            {!userHasAccess && (
              <InlineNotification status='error' size='small' message={t('This voicemail is restricted.')} />
            )}
          </section>
        }
        id={notification.id}
        location={notification.location}
        timestamp={notification.timestamp}
        notificationType={notification.type}
        {...rest}
      ></BaseNotificationInner>
    </BaseNotificationOuter>
  );
};
